<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch, markRaw } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import mockSidebar from '@/components/tutorialmocks/Sidebar.vue'
import mocklocationsandpointsmap from '@/components/tutorialmocks/locationsandpointsmap.vue'
import mockPointspecificlist from '@/components/tutorialmocks/pointspecificlist.vue'
import mockChartwindow from '@/components/tutorialmocks/chartwindow.vue'
import {
  getTutorialTextboxPosition,
  getTutorialTextboxWidth,
  type TutorialTextboxSide
} from '@/utils/tutorialPlacement'

const { t } = useI18n()
const store = useStore()
const route = useRoute()
const publicPath = window.location.origin

const mockelements = ref([
  {
    element: 'sidebar',
    module: markRaw(mockSidebar),
    show: false,
    pages: ['all'],
    elementToHide: 'originalsidebar'
  },
  {
    element: 'locationsandpoints',
    module: markRaw(mocklocationsandpointsmap),
    show: false,
    pages: ['map.locations'],
  },
  {
    element: 'pointspecificlist',
    module: markRaw(mockPointspecificlist),
    show: false,
    pages: ['map.locations'],
  },
  {
    element: 'mockchartwindow',
    module: markRaw(mockChartwindow),
    show: false,
    pages: ['map.locations'],
  }
])

type TutorialElement = {
  element: string
  textbox: TutorialTextboxSide
  title: string
  message: string
  custom?: boolean
  finished?: boolean
}

const tutorialelements: TutorialElement[] = [
  {
    element: "sidebar",
    textbox: "right",
    title: t('Tutorials.Sidebartitle'),
    message: t('Tutorials.Sidebar'),
    custom: true,
  },
  {
    element: "addresssearch",
    textbox: "bottom",
    title: t('Tutorials.Addresssearchtitle'),
    message: t('Tutorials.Addresssearch'),
  },
  {
    element: "projectslist",
    textbox: "top",
    title: t('Tutorials.Projectstitle'),
    message: t('Tutorials.Projectslist'),
  },
  {
    element: "sectionslist",
    textbox: "top",
    title: t('Tutorials.Sectionstitle'),
    message: t('Tutorials.Sectionslist'),
  },
  {
    element: "pointslist",
    textbox: "top",
    title: t('Tutorials.Pointstitle'),
    message: t('Tutorials.Pointslist'),
  },
  {
    // enkel voor projecten/deelprojecten
    element: "layersmenu",
    textbox: "top",
    title: t('Tutorials.Layersmenutitle'),
    message: t('Tutorials.Layersmenu'),
  },
  {
    element: "streetview",
    textbox: "top",
    title: t('Tutorials.Streetviewtitle'),
    message: t('Tutorials.Streetview'),
  },
  {
    element: "locationsandpoints",
    textbox: "bottom",
    title: t('Tutorials.Locationsandpointstitle'),
    message: t('Tutorials.Locationsandpoints'),
    custom: true,
  },
  {
    element: "pointspecificlist",
    textbox: "top",
    title: t('Tutorials.Pointspecificlisttitle'),
    message: t('Tutorials.Pointspecificlist'),
    custom: true
  },
  {
    element: "mockchartwindow",
    textbox: "bottom",
    title: t('Tutorials.Chartwindowtitle'),
    message: t('Tutorials.Chartwindow'),
    custom: true
  },
  // ALTIJD ALS LAATSTE HOUDEN
  {
    element: "emptydiv",
    textbox: "bottom",
    title: t('Tutorials.Tutorialfinishedtitle'),
    message: t('Tutorials.Tutorialfinished'),
    finished: true,
  }
]
const currentelement = ref(-1);
const disabledelement = ref<{ element: HTMLElement | null, originalstate: string }>({element: null, originalstate: ''})
const placementRevision = ref(0)

const refreshTutorialPlacement = () => {
  placementRevision.value += 1
}

const nextelement = async() => {
  mockelements.value.forEach((element) => {
    element.show = false
  })
  if (disabledelement.value.element) {
    disabledelement.value.element.style.opacity = disabledelement.value.originalstate
  }
  for (let i = currentelement.value + 1; i < Object.keys(tutorialelements).length; i++) {
    if (tutorialelements[i].custom) {
      const moduleIndex = mockelements.value.findIndex((element) => element.element === tutorialelements[i].element)
      if (moduleIndex !== -1 && (mockelements.value[moduleIndex].pages.includes('all') || mockelements.value[moduleIndex].pages.includes(String(route.name ?? '')))) {
        if (mockelements.value[moduleIndex].elementToHide) {
          const elementToHide = document.getElementById(mockelements.value[moduleIndex].elementToHide);
          disabledelement.value = {element: elementToHide, originalstate: elementToHide?.style.opacity ?? ''}
          if (elementToHide) {
            elementToHide.style.opacity = '0'
          }
        }
        mockelements.value[moduleIndex].show = true
        await nextTick()
        currentelement.value = i
        break
      }
    } else {
      const element = tutorialelements[i].element;
      const testelement = document.getElementById(element);
      if (testelement) {
        currentelement.value = i
        break
      }
    }
  }
  await nextTick()
  refreshTutorialPlacement()
}

const stoptutorial = () => {
  mockelements.value.forEach((element) => {
    element.show = false
  })
  if (disabledelement.value.element) {
    disabledelement.value.element.style.opacity = disabledelement.value.originalstate
  }
  currentelement.value = -1
  store.dispatch('interactabletutorial/stop')
}

const getClass = (element: any) => {
  const testelement = document.getElementById(element);
  const boundingClient = testelement?.getBoundingClientRect();
  const test = buildClass(boundingClient)
  return test
}

const getSize = (element: any) => {
  const testelement = document.getElementById(element);
  const boundingClient = testelement?.getBoundingClientRect();
  const test = buildSize(boundingClient)
  return test
}

const getTextboxStyle = (element: { element: string, textbox: TutorialTextboxSide }) => {
  const revision = placementRevision.value

  const targetElement = document.getElementById(element.element);
  const targetRect = targetElement?.getBoundingClientRect();
  const textboxelement = document.getElementById('textbox');

  if (!targetRect) {
    return '';
  }

  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  }
  const width = getTutorialTextboxWidth(targetRect, viewport)
  const textboxRect = textboxelement?.getBoundingClientRect()
  const textboxSize = {
    width,
    height: textboxRect?.height || 160,
  }
  const position = getTutorialTextboxPosition(targetRect, element.textbox, textboxSize, viewport)

  return `position: absolute; top: ${position.top}px; left: ${position.left}px; width: ${width}px; --tutorial-placement-revision: ${revision};`
}

const buildSize = (boundingClient: any) => {
  if (boundingClient && boundingClient.height !== undefined && boundingClient.width !== undefined) {
    return `height: ${boundingClient.height}px; width: ${boundingClient.width}px;`;
  } else {
    return '';
  }
}

const buildClass = (boundingClient: any) => {
  if (boundingClient && boundingClient.top !== undefined && boundingClient.left !== undefined) {
    return `position: absolute; top: ${boundingClient.top}px; left: ${boundingClient.left}px;`;
  } else {
    return '';
  }
}

watch(() => store.getters['interactabletutorial/started'], (value) => {
  if (value === true) {
    nextelement()
  }
})

onMounted(() => {
  window.addEventListener('resize', refreshTutorialPlacement)
  window.addEventListener('scroll', refreshTutorialPlacement, true)
})

onUnmounted(() => {
  window.removeEventListener('resize', refreshTutorialPlacement)
  window.removeEventListener('scroll', refreshTutorialPlacement, true)
})
</script>

<template>
  <div class="absolute mx-6 my-2" id="emptydiv"></div>
  <Teleport to="#app">
   <div v-if="store.getters['interactabletutorial/started'] && currentelement >= 0" class="fixed inset-0 z-80 w-full pointer-events-none select-none" id="interactabletutorial">
      <div :style="getClass(tutorialelements[currentelement].element)" @click="stoptutorial">
        <div id="shadowbox" class="bg-transparent rounded-lg pointer-events-none ring-3 ring-black/10 " style="box-shadow: 0 0 0 5000px rgba(0, 0, 0, 0.8); border-radius: 0.5rem;" :style="getSize(tutorialelements[currentelement].element)"></div>
      </div>
      <div style="transition: top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease;" :style="getTextboxStyle(tutorialelements[currentelement])">
        <div id="textbox" class="bg-white rounded-lg shadow-google ring-3 ring-black/10 p-2.5 pointer-events-auto w-full">
          <div class="flex justify-between">
            <h3 class="text-md font-semibold text-gray-900">{{ tutorialelements[currentelement].title }}</h3>
          </div>
          <p class="text-sm text-gray-600">{{ tutorialelements[currentelement].message }}</p>
          <p v-if="tutorialelements[currentelement].finished" class="text-sm text-gray-600">{{ t('need more in-depth information') }}?
            <RouterLink :to="{ name: 'manuals'} " class="text-primary-600 hover:underline">{{ t('Manual', 2) }}</RouterLink>
          </p>
          <div class="flex mt-4 justify-between ">
            <button id="stopbutton" class="btn-danger-inverted first-letter:capitalize" @click="stoptutorial">
              {{ t('stop') }}
            </button>
            <button id="nextbutton" v-if="!tutorialelements[currentelement].finished" class="btn-primary first-letter:capitalize" @click="nextelement">
              {{ t('next') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
  <div v-for="element in mockelements" :key="element.element">
    <component class="z-50" :show="element.show" :is="element.module" />
  </div>
</template>
<style scoped>
.smooth-transition {
  transition: all 0.3s ease-in-out;
}
</style>
